import { action, makeObservable, observable, flow, computed } from "mobx";
import dayjs from "dayjs";
import { API, graphqlOperation } from "aws-amplify";
import {
  getAgent,
  getConversationChatAssistant,
  getCustomerMessages,
} from "../../graphql/queries";
import { onChatAssistantResponse } from "../../graphql/subscriptions";
import { AssistantFnTypes, UCPAgentVersion } from "../../utils/enums";

class AssistanceUCPStore {
  // Chat properties
  conversationHistory = [];
  newMessage = "";
  agentIsTyping = false;
  showStopButton = false;
  showSpinner = false;
  isSendMessageClicked = false;
  conversationID = "";
  attachment = "";
  isLoadingMessages = false;
  isLoadingConcierge = false;
  conciergeAgent = null;
  agentParams = {
    message: "",
    campaign_id: "",
    campaign_version: "",
    agent: "",
  };
  isError = false;
  agentResponseSubscription = null;
  runID = "";
  threadID = "";
  customer = "";

  // Timeline properties
  timelineMessages = [];
  timelineMatches = [];
  loading = false;
  lastEvaluatedKey = null;

  // Scroll properties
  scrollPosition = 0;
  containerHeight = 0;
  containerScrollHeight = 0;

  // Shared properties
  selectedTab = "Chat";

  // Add these new properties
  showSuggestions = false;
  currentCardType = null;

  isCustomerMessagesError = false;

  // Add this to the class properties at the top
  isStreaming = false;
  agentVersion = "default";
  
  // Search properties
  searchTerm = "";
  highlightedResult = -1;

  // Referencia al historyAgentStore
  historyAgentStore = null;

  // Conversaciones filtradas por contactId
  filteredConversations = [];

  constructor() {
    makeObservable(this, {
      // Chat observables
      conversationHistory: observable,
      newMessage: observable,
      agentIsTyping: observable,
      showStopButton: observable,
      showSpinner: observable,
      isSendMessageClicked: observable,
      conversationID: observable,
      attachment: observable,
      isLoadingConcierge: observable,
      isLoadingMessages: observable,
      conciergeAgent: observable,
      agentParams: observable,
      isError: observable,
      timelineMessages: observable,
      timelineMatches: observable,
      loading: observable,
      lastEvaluatedKey: observable,
      runID: observable,
      threadID: observable,
      customer: observable,
      // Scroll observables
      scrollPosition: observable,
      containerHeight: observable,
      containerScrollHeight: observable,
      isCustomerMessagesError: observable,

      // Shared observables
      selectedTab: observable,

      // Add these new observables
      showSuggestions: observable,
      currentCardType: observable,

      // Add this to the constructor's makeObservable
      isStreaming: observable,
      
      // Search observables
      searchTerm: observable,
      highlightedResult: observable,

      // Store reference
      historyAgentStore: observable,

      // Filtered conversations
      filteredConversations: observable,

      // Chat actions
      setConversationHistory: action,
      addMessage: action,
      setNewMessage: action,
      setAgentIsTyping: action,
      setShowStopButton: action,
      setShowSpinner: action,
      setIsSendMessageClicked: action,
      setConversationID: action,
      setAttachment: action,
      setIsLoadingConcierge: action,
      setConciergeAgent: action,
      setAgentParams: action,
      setIsError: action,
      setCustomer: action,
      cleanupConversation: action,
      resetTimelineState: action,

      // Timeline actions
      setTimelineMessages: action,
      setLoading: action,
      setLastEvaluatedKey: action,

      // Scroll actions
      setScrollPosition: action,
      setContainerDimensions: action,
      updateScroll: action,

      // Shared actions
      setSelectedTab: action,

      // Add these new actions
      setShowSuggestions: action,
      setCurrentCardType: action,

      // Flows
      fetchConciergeAgent: flow,
      handleSendMessage: flow,
      fetchCustomerMessages: flow,

      // Add this action method
      setIsStreaming: action,

      agentVersion: observable,
      setAgentVersion: action,
      
      // Search actions
      setSearchTerm: action,
      setHighlightedResult: action,
      resetSearch: action,

      // Filtered conversations
      setFilteredConversations: action,

      // Computed properties
      filteredCount: computed,
      totalCount: computed,

      // Timeline matches
      setTimelineMatches: action,
    });
  }

  cleanupConversation = () => {
    this.setConversationHistory([]);
    this.setNewMessage("");
    this.setAgentIsTyping(false);
    this.setShowStopButton(false);
    this.setShowSpinner(false);
    this.setIsSendMessageClicked(false);
    this.setConversationID("");
    this.setAttachment("");
    this.runID = "";
    this.threadID = "";
    this.setShowSuggestions(false);
    this.setCurrentCardType(null);
    this.resetSearch(); // Reset search when cleaning up conversation

    if (this.agentResponseSubscription) {
      this.agentResponseSubscription.unsubscribe();
      this.agentResponseSubscription = null;
    }

    // Reset streaming status
    this.setIsStreaming(false);
  };

  // Chat methods
  setConversationHistory(history) {
    this.conversationHistory = history;
  }

  addMessage(message) {
    this.conversationHistory.push(message);
  }

  setNewMessage(message) {
    this.newMessage = message;
  }

  setAgentIsTyping(isTyping) {
    this.agentIsTyping = isTyping;
  }

  setShowStopButton(isShow) {
    this.showStopButton = isShow;
  }

  setShowSpinner(show) {
    this.showSpinner = show;
  }

  setIsSendMessageClicked(clicked) {
    this.isSendMessageClicked = clicked;
  }

  setConversationID(id) {
    this.conversationID = id;
  }

  setAttachment(attachment) {
    this.attachment = attachment;
  }

  setIsLoadingConcierge(isLoading) {
    this.isLoadingConcierge = isLoading;
  }

  setConciergeAgent(agent) {
    this.conciergeAgent = agent;
  }

  setAgentParams(params) {
    this.agentParams = { ...this.agentParams, ...params };
  }

  setIsError(isError) {
    this.isError = isError;
  }

  setTimelineAgentIsTyping(isTyping) {
    this.timelineAgentIsTyping = isTyping;
  }

  // Shared methods
  setSelectedTab(tab) {
    this.selectedTab = tab;
  }

  setAgentVersion(version) {
    this.agentVersion = version;
  }

  resetTimelineState = () => {
    this.timelineMessages = [];
    this.lastEvaluatedKey = null;
    this.isCustomerMessagesError = false;
    this.isLoadingMessages = false;
  };

  *fetchConciergeAgent(customer) {
    try {
      this.setIsLoadingConcierge(true);
      const response = yield API.graphql({
        query: getAgent,
        variables: { input: { customer_id: customer, domain: "ucp" } },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });
      const agents = JSON.parse(response.data.getAgent.body).filter(
        (agent) => agent.provider === "openai",
      );
      const conciergeAgent = agents[0];

      if (conciergeAgent) {
        this.setConciergeAgent(conciergeAgent);
        this.setAgentParams({ agent: conciergeAgent?.id });
      } else {
        this.setConversationHistory([
          {
            message: "Error: UCP agent for this account does not exist.",
            type: "received",
            time: dayjs().format("HH:mm"),
          },
        ]);
      }
    } catch (error) {
      this.setIsError(true);
    } finally {
      this.setIsLoadingConcierge(false);
    }
  }

  *handleSendMessage(message, contactInfo, selectedAccount, isAnnUCP, ucpData, userData) {
    const messageToSend = message || this.newMessage;
    if (messageToSend.trim() === "") return;
    this.setIsSendMessageClicked(true);

    this.addMessage({
      message: messageToSend,
      type: "sent",
      time: dayjs().format("HH:mm"),
    });

    this.setNewMessage("");
    const vin = isAnnUCP ? ucpData.selectedCar?.vin : ucpData.selectedCar;

    const messageParam = `Context information: customer = ${contactInfo.contact_full_name},
      ${!isAnnUCP ? `, VIN = ${vin}` : ""}.
      This is a question based only on context customer information (do not query information or answer questions unrelated to it)`;

    const assistant_fn_type = this.agentVersion === UCPAgentVersion.BETA ? AssistantFnTypes.UCP_LANGCHAIN : "";

    const customer_name = isAnnUCP ? ucpData.ucp.name : contactInfo.contact_full_name;

    const segment_id = isAnnUCP ? ucpData.ucp.segment_id : contactInfo.contact_id;

    const params = {
      ...this.agentParams,
      message: messageToSend,
      metadata: JSON.stringify({
        segment_id,
        customer_name,
        vin,
        additional_message: messageParam
      }),
      customer_id: selectedAccount.id,
      attachment: this.attachment,
      conversation_id: this.conversationID,
      assistant_fn_type,
    };

    let openAiAnswer = null;
    let resultConversationOpenAi = null;
    try {
      this.setAgentIsTyping(true);
      resultConversationOpenAi = yield API.graphql({
        query: getConversationChatAssistant,
        variables: { input: params },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });
      if (!resultConversationOpenAi.errors?.length) {
        // Just set the typing indicator state - don't add an empty message

        const apiResponse = resultConversationOpenAi.data.getConversationChatAssistant;

        const parseResponseBody = (responseBody) => JSON.parse(responseBody);

        this.setAttachment("");

        this.agentResponseSubscription = API.graphql({
          ...graphqlOperation(onChatAssistantResponse, {
            id: apiResponse.id,
          }),
          authMode: "AMAZON_COGNITO_USER_POOLS",
        }).subscribe({
          next: ({ value }) => {
            const subApiResponse = value.data.onChatAssistantResponse;
            const body = parseResponseBody(subApiResponse.body);
            openAiAnswer = body.answer;
            this.setShowSpinner(false);
            this.setConversationID(body.conversation_id);
            this.threadID = body.thread_id;
            this.runID = body.run_id;
            this.setShowStopButton(true);

            // Set streaming status based on response status
            const isStreaming = subApiResponse.status === "stream";
            this.setIsStreaming(isStreaming);

            const currentMessages = this.conversationHistory;
            const lastMessage = currentMessages[currentMessages.length - 1];

            if (subApiResponse.status === "done" || subApiResponse.status === "guardrail") {
              this.setShowStopButton(false);
              this.setIsStreaming(false);
              this.setAgentIsTyping(false);

              // Add the final complete message
              this.addMessage({
                message: openAiAnswer,
                type: "received",
                time: dayjs().format("HH:mm"),
                isTyping: false
              });

              if (this.agentResponseSubscription) {
                this.agentResponseSubscription.unsubscribe();
                this.agentResponseSubscription = null;
              }
              this.setIsSendMessageClicked(false);
            } else {
              // Only add/update message if we have actual content
              if (openAiAnswer) {
                if (!lastMessage || lastMessage.type !== "received") {
                  // First chunk of actual text
                  this.addMessage({
                    message: openAiAnswer,
                    type: "received",
                    time: dayjs().format("HH:mm"),
                    isTyping: true
                  });
                } else {
                  // Update existing message with new chunk
                  this.setConversationHistory([
                    ...currentMessages.slice(0, -1),
                    {
                      ...lastMessage,
                      message: lastMessage.message + openAiAnswer,
                      isTyping: true
                    },
                  ]);
                }
              }
            }
          },
          error: (error) => {
            this.isError = true;
            this.setShowSpinner(false);
            this.setAgentIsTyping(false);
            this.addMessage({
              message: "Oops! Something went wrong. Please try again.",
              type: "received",
              time: dayjs().format("HH:mm"),
            });
          },
        });
      } else {
        this.setAgentIsTyping(false);
      }
    } catch (err) {
      this.isError = true;
      this.addMessage({
        message: "Oops! Something went wrong. Please try again.",
        type: "received",
        time: dayjs().format("HH:mm"),
      });
    }
  }

  setTimelineMessages(messages) {
    // Asegurar que los mensajes sean tratados como una nueva referencia
    this.timelineMessages = Array.isArray(messages) ? [...messages] : [];
  }

  setLoading(loading) {
    this.loading = loading;
  }

  setLastEvaluatedKey(key) {
    this.lastEvaluatedKey = key;
  }

  // Scroll methods
  setScrollPosition = (position) => {
    this.scrollPosition = position;
  };

  setContainerDimensions = (height, scrollHeight) => {
    this.containerHeight = height;
    this.containerScrollHeight = scrollHeight;
  };

  updateScroll = (containerRef) => {
    if (containerRef) {
      const scrollTop = this.containerScrollHeight - this.scrollPosition;
      containerRef.scrollTop = scrollTop > 0 ? scrollTop : 0;
    }
  };

  setCustomer = (customer) => {
    this.customer = customer;
  };

  // Add these new methods
  setShowSuggestions(value) {
    this.showSuggestions = value;
  }

  setCurrentCardType(type) {
    this.currentCardType = type;
  }

  // Add this action method
  setIsStreaming(streaming) {
    this.isStreaming = streaming;
  }

  /**
   * Fetches customer messages. If there are no messages, it uses mock data.
   * @param {string} customer_id - The id of the customer
   * @param {string} contact_id - The id of the contact
   * @param {number} [last_evaluated_item=0] - The last evaluated item to start fetching from
   * @returns {Promise<void>}
   */
  *fetchCustomerMessages(customer_id, contact_id, last_evaluated_item = 0) {
    try {
      // Start loading process
      this.isLoadingMessages = true;
      this.isCustomerMessagesError = false;

      // Make a GraphQL request to fetch customer messages
      const response = yield API.graphql({
        query: getCustomerMessages,
        variables: {
          input: {
            customer_id, // Customer ID parameter
            contact_id, // Contact ID parameter
            last_evaluated_item, // Last evaluated item parameter
          },
        },
        authMode: "AMAZON_COGNITO_USER_POOLS", // Authentication mode
      });

      // Parse the response data
      const data = JSON.parse(response.data?.getCustomerMessages?.body);
      // Get mock data in case of no real messages

      // Determine which messages to use, empty or real
      const messages = data?.messages || [];

      // Update timeline messages, appending if not the first request
      this.setTimelineMessages(
        !last_evaluated_item
          ? messages
          : [...this.timelineMessages, ...messages]
      );

      this.setLastEvaluatedKey(data?.last_evaluated_item);
    } catch (error) {
      this.isCustomerMessagesError = true;
    } finally {
      this.isLoadingMessages = false;
    }
  }

  // Search methods
  setSearchTerm(term) {
    this.searchTerm = term;
    this.highlightedResult = -1;
  }
  
  setHighlightedResult(index) {
    this.highlightedResult = index;
  }
  
  resetSearch() {
    this.searchTerm = "";
    this.highlightedResult = -1;
  }
  
  //  Method to set filtered conversations by contactId
  setFilteredConversations(conversations) {
    this.filteredConversations = conversations;
  }

  // Update getters to use filteredConversations
  get filteredCount() {
    if (!this.searchTerm || !this.filteredConversations || this.filteredConversations.length === 0) {
      return 0;
    }
    
    const filtered = this.filteredConversations.filter(conversation => 
      conversation.title.toLowerCase().includes(this.searchTerm.toLowerCase())
    );
    
    return filtered.length;
  }
  
  get totalCount() {
    if (!this.filteredConversations) {
      return 0;
    }
    
    return this.filteredConversations.length;
  }

  // Add this action method
  setTimelineMatches(matches) {
    this.timelineMatches = matches;
  }
}

export default AssistanceUCPStore;
